import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAllCampaigns } from "@/hooks/queries/campaigns";
import { useGetCompaniesList } from "@/hooks/queries/useCompanies";
import Loader from "@/components/Loader";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { Eye, LucideScreenShare } from "lucide-react";
import {
  CalendarIcon,
  DocumentIcon,
  PencilIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";

import useToastStore from "@/components/Common/Toast/toastStore";
import {
  MoreVertical,
  Edit,
  Briefcase,
  PlusCircle,
  Search,
  ChevronDown,
  Plus,
  Calendar,
  Clock,
  Trash2,
  Copy,
  Download,
  X,
  Users,
  KeyRound as Pound,
  CheckCircle,
  XCircle,
  FileText,
} from "lucide-react";
import { campaignsService } from "@/services/campaignsService";
import CampaignTable from "@/components/campaign/CampaignTable";
import { CAMPAIGN_STATUS } from "@/config";
import { companiesService } from "@/services/companiesService";
import Dropdown from "@/components/ui/Dropdown";
import Pagination from "@/components/Common/Pagination";
import { useDebounce } from "@/utils/helpers";
import useGlobalStore from "@/hooks/stores/useGlobalStore";
import { PER_PAGE } from "@/config";
import usePaginationStore from "@/hooks/stores/usePaginationStore";

const AllCampaigns = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState();
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const { showToast } = useToastStore;
  const { getPerPage, setPerPage } = usePaginationStore();
  const { activeTab, setActiveTab } = useGlobalStore();
  const [columnFilters, setColumnFilters] = useState({
    title: "",
    status: "",
    start_date: "",
    end_date: "",
  });

  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: getPerPage("all-campaigns-table"),
    total: 0,
    last_page: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [tempSearchTerm, setTempSearchTerm] = useState("");

  const [activeMenu, setActiveMenu] = useState(null);
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const { data } = useGetCompaniesList() || {};
  const companies = data?.companies || [];
  const message = data?.message || "";

  const handleVacanciesClick = (campaign) => {
    setActiveTab("vacancies");
    navigate(`/companies/view/${campaign.company_id}`, {
      state: {
        campaignId: campaign.id,
      },
    });
  };

  const updateSearchTerm = (term) => {
    setSearchTerm(term);
  };

  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  useEffect(() => {
    if (Array.isArray(companies)) {
      setFilteredCompanies(companies);
    }
  }, [companies]);

  useEffect(() => {
    fetchAllCampaigns();
  }, [
    serverPagination.current_page,
    serverPagination.per_page,
    searchTerm,
    selectedCompany,
  ]);

  const fetchAllCampaigns = async () => {
    setIsLoading(true);
    try {
      const { campaigns, pagination } = await campaignsService.getAllCampaigns({
        company_id: selectedCompany,
        page: serverPagination.current_page,
        per_page: serverPagination.per_page,
        search: searchTerm,
        status: [1, 0],
      });

      setCampaigns(campaigns);
      setFilteredCampaigns(campaigns);
      setServerPagination((prev) => ({ ...prev, ...pagination }));
    } catch (error) {
      console.error("Error fetching all campaigns:", error);
      showToast({ message: "Failed to fetch campaigns", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setServerPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
  };

  // Handle pagination dropdown change
  const handlePaginationChange = (selectedOption) => {
    const newPerPage = selectedOption.value;
    setPerPage("all-campaigns-table", newPerPage);
    setServerPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1, // Reset to first page when changing items per page
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      await campaignsService.updateCampaignStatus(campaignId, {
        status: newStatus,
      });
      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === campaignId
            ? { ...campaign, status: newStatus }
            : campaign,
        ),
      );
      showToast({
        message: "Campaign status updated successfully",
        type: "success",
      });
    } catch (error) {
      console.error("Error updating campaign status:", error);
      showToast({
        message: error.message || "Failed to update campaign status",
        type: "error",
      });
    }
  };

  const handleCompanyChange = (selected) => {
    setSelectedCompany(selected?.value || null);
    if (serverPagination.current_page > 1)
      setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const dropdownOptions = [
    { label: "All Companies", value: null },
    ...filteredCompanies.map((company) => ({
      label: company.name,
      value: company.id,
    })),
  ];

  const handleEdit = (id) => {
    navigate(`/campaigns/${id}`);
  };

  return (
    <div className="w-[90%] mx-auto pt-12 pb-24 flex flex-col justify-start items-start gap-8 px-8">
      {/* Page Header */}
      <div className="self-stretch flex flex-col justify-start items-start gap-4">
        <div className="self-stretch inline-flex justify-start items-start gap-4 flex-wrap content-start">
          <div className="flex-1 min-w-80 inline-flex flex-col justify-start items-start gap-1">
            <h1 className="self-stretch justify-start text-foreground text-2xl font-semibold font-['Inter'] leading-8">
              Campaigns
            </h1>
            <p className="self-stretch justify-start text-muted-foreground text-base font-normal font-['Inter'] leading-6">
              Oversee recruitment campaigns, monitor activity, and manage hiring
              across all roles.
            </p>
          </div>
        </div>
      </div>

      {/* Controls Section: Search and Dropdown */}
      <div className="self-stretch flex flex-col justify-start items-start gap-6">
        <div className="self-stretch inline-flex justify-between items-start gap-4 flex-wrap">
          {/* Search Box */}
          <div className="w-96 flex flex-col justify-start items-start gap-1.5 font-['Inter']">
            <div className="flex flex-col justify-start items-start gap-1.5">
              <div className="inline-flex justify-start items-center gap-1">
                <span className="text-muted-foreground text-sm font-medium leading-5">
                  Search
                </span>
                <span className="text-foreground text-sm font-medium leading-5">
                  *
                </span>
                <div className="w-4 h-4 text-muted-foreground">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-full h-full"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4" />
                    <path d="M12 8h.01" />
                  </svg>
                </div>
              </div>
              <div className="self-stretch px-3 py-2 bg-card rounded-lg shadow-sm border border-input focus-within:ring-2 focus-within:ring-secondary/20 focus-within:border-secondary transition-all inline-flex justify-start items-center gap-2 overflow-hidden">
                <div className="flex-1 flex justify-start items-center gap-2">
                  <Search className="w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search"
                    value={tempSearchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="flex-1 bg-transparent border-none outline-none text-foreground placeholder-muted-foreground text-base font-normal font-['Inter'] leading-6"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Company Filter Dropdown */}
          <div className="flex flex-col gap-1.5">
            <div className="relative z-10">
              <Dropdown
                options={dropdownOptions}
                value={
                  selectedCompany
                    ? {
                        label: filteredCompanies.find(
                          (c) => c.id === selectedCompany,
                        )?.name,
                        value: selectedCompany,
                      }
                    : { label: "All Companies", value: null }
                }
                onChange={handleCompanyChange}
                placeholder="All Companies"
                searchable={true}
                className="w-[240px]"
                dropdownWidth="w-[240px]"
                triggerClassName="!bg-primary-dark !h-8 !px-2.5 !py-1.5 !rounded-lg !border-none shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] shadow-[inset_0px_-2px_0px_0px_rgba(10,13,18,0.05)] shadow-[inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] outline outline-2 outline-offset-[-2px] outline-white/10 !text-white"
                customArrow={<ChevronDown className="w-4 h-4 text-white" />}
              />
            </div>
          </div>
        </div>

        {/* Campaign Table */}
        <div className="self-stretch">
          {isLoading ? (
            <div className="flex w-full min-h-[400px] items-center justify-center bg-card rounded-xl shadow-sm border border-border">
              <Loader bgColor="transparent" />
            </div>
          ) : (
            <CampaignTable
              campaigns={filteredCampaigns}
              handleStatusChange={handleStatusChange}
              handleEdit={handleEdit}
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              onVacanciesClick={handleVacanciesClick}
              handleSearchChange={handleSearchChange}
              searchTerm={tempSearchTerm}
              noCampaignMessage="No campaigns found"
              serverPagination={serverPagination}
              onPageChange={handlePageChange}
              onPerPageChange={handlePaginationChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AllCampaigns;
